import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';
import {
  TUrlInputRef
} from '@ppmcore/seven-ppm-core-shared-components-react/dist/cjs/types/lib/components/url-input/url-input';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  DiscardModal,
  EIconName,
  InputReadOnly,
  UrlInput
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Thunks
import {
  // checkAvailabilityPersonalLink,
  updateCounsellorPersonalLink
} from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

// Styles
import './personal-link.scss';

type TPersonalLinkProps = {
  personalLinkEditable: string;
  personalLinkNotEditable: string;
  inactiveCounsellor: boolean;
  worker_id: string | number;
};

export const PersonalLink: FC<TPersonalLinkProps> = (
  {personalLinkEditable, personalLinkNotEditable, inactiveCounsellor, worker_id}: TPersonalLinkProps
) => {
  const [form] = Form.useForm<{
    personal_link: string
  }>();
  const inputUrlRef = useRef<TUrlInputRef>(null);

  const [discardOpen, setDiscardOpen] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const onValuesChange = (_: {[key: string]: string | string[]}, {personal_link}: {
    personal_link: string
  }): void => {
    const hasChanges = personalLinkEditable !== personal_link && !!inputUrlRef.current?.isValid();
    setHasChanges(hasChanges);
    inputUrlRef.current?.changeErrorMessage('');
    inputUrlRef.current?.changeValidStatus(!!inputUrlRef.current?.isValid(personal_link));
  }

  const toggleEditMode = (): void => {
    setEditMode(!editMode);
  }

  const toggleDiscardModal = (): void => {
    setDiscardOpen(!discardOpen);
  }

  const onCancelChangesHandler = (): void => {
    if (hasChanges) {
      toggleDiscardModal();
      return;
    }
    form.setFieldsValue({
      personal_link: personalLinkEditable ?? ''
    });
    toggleEditMode();
  }

  const onDiscardChangesHandler = (state: boolean): void => {
    if (state) {
      setHasChanges(false);
      toggleEditMode();
      toggleDiscardModal();
      form.setFieldsValue({
        personal_link: personalLinkEditable ?? ''
      });
      return;
    }
    toggleDiscardModal();
  }

  const onCopyHandler = (): void => {
    dispatch(openNotification({
      type: 'success',
      description: 'Successfully copied!'
    }));
  }

  const onCopyErrorHandler = (): void => {
    dispatch(openNotification({
      type: 'error',
      description: 'Copy failed! Try again'
    }));
  }

  const saveChanges = async (): Promise<void> => {
    const {personal_link} = form.getFieldsValue();
    const trimUrl = personal_link.trim();

    setSavingChanges(true);
    const {payload} = await dispatch(updateCounsellorPersonalLink({personal_link: trimUrl, worker_id}));
    setSavingChanges(false);

    if (!payload) {
      inputUrlRef.current?.changeErrorMessage('That URL is not available. Please, try a different URL.');
      inputUrlRef.current?.changeValidStatus(false);
      setHasChanges(false);
      return;
    }
    setEditMode(false);
    setHasChanges(false);
  }

  useEffect(() => {
    form.setFieldsValue({personal_link: personalLinkEditable});
  }, [personalLinkNotEditable, personalLinkEditable]);

  return (
    <div className="personal-link">
      <DiscardModal
        open={discardOpen}
        onCancel={onDiscardChangesHandler}
        title={'Are you sure you want to discard changes?'}
      />
      <div className="personal-link--label personal-data--label">
        <div className="label-title">
          Personal Link <CustomTooltip
          title={'This is the unique URL of a Consultant\'s Paypertok Profile, where users can access their information, consultation options, prices, and call or message to request a consultation.\n' +
            'To share, click the copy icon and paste it into emails, social media, or other channels.\n' +
            'Note: The page is private and not searchable by anyone who doesn\'t have the link.'}>
          <CustomIcon size={'size-md'} name={EIconName.Info}/>
        </CustomTooltip>
        </div>
        <div className="label-actions">
          {!editMode &&
            <CustomButton type={'text'}
                          text={'Edit'}
                          size={'small'}
                          onClick={toggleEditMode}/>
          }

          {editMode && <>
            {hasChanges && <CustomButton type={'primary'}
                                         text={'Save'}
                                         size={'small'}
                                         loading={savingChanges}
                                         onClick={saveChanges}/>}
            <CustomButton type={'text'}
                          text={'Cancel'}
                          size={'small'}
                          disabled={savingChanges}
                          onClick={onCancelChangesHandler}/>
          </>}
        </div>
      </div>
      <div className="personal-link--content personal-data--content">
        <Form className="content-item" form={form}
              autoComplete={'off'}
              onValuesChange={onValuesChange}>
          {
            !editMode && <InputReadOnly
              label={'Personal Link'}
              value={personalLinkEditable ? `${personalLinkNotEditable}${personalLinkEditable}` : ''}
              icon={<CustomIcon name={EIconName.ContentCopy}/>}
              isCopyIcon={true}
              textEllipsis={true}
              unselected={inactiveCounsellor}
              onCopy={onCopyHandler}
              onCopyError={onCopyErrorHandler}
            />
          }

          {
            editMode &&
            <Form.Item name={'personal_link'}>
              <UrlInput ref={inputUrlRef}
                        urlPrefix={personalLinkNotEditable} placeholder={'Personal Link'}/>
            </Form.Item>
          }
        </Form>
      </div>
    </div>
  );
}
